import React, { useEffect, useState } from "react";
import { FaPlay, FaEye, FaCheckCircle, FaTimesCircle, FaSpinner, FaHourglassHalf, FaImage, FaSearch, FaCopy } from "react-icons/fa";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import DescriptionViewer from '../../Common/DescriptionViewer';

const CompletedAdvertisements = () => {
  const [completedAds, setCompletedAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkingViews, setCheckingViews] = useState({});
  const [viewResults, setViewResults] = useState({});
  const [searchTerm, setSearchTerm] = useState(''); // State for Searching by ID

  // Pagination states for advertisements
  const [currentPage, setCurrentPage] = useState(1);
  const adsPerPage = 50;

  // Format date as dd/mm/yy hh:mm:ss
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = String(date.getFullYear()).slice(-2);

    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");

    return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
  };

  // Handle page change for advertisements
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const fetchCompletedAds = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/advertiser/get/completed/ads`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      console.log("Fetched completed ads:", response.data);
      setCompletedAds(response.data.completedProofs);
    } catch (err) {
      console.error("Error fetching advertisements:", err);
    } finally {
      setLoading(false);
    }
  };

  // Function to check video views
  const checkVideoViews = async (adId) => {
    setCheckingViews(prev => ({ ...prev, [adId]: true }));

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/advertiser/check/video-views/${adId}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.data.success) {
        setViewResults(prev => ({ ...prev, [adId]: response.data.data }));
        toast.success("Video views checked successfully!");
      } else {
        toast.error(response.data.message || "Failed to check video views");
      }
    } catch (error) {
      console.error("Error checking video views:", error);
      toast.error("Error checking video views. Please try again.");
    } finally {
      setCheckingViews(prev => ({ ...prev, [adId]: false }));
    }
  };

  useEffect(() => {
    fetchCompletedAds();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const filteredAds = completedAds.filter(ad => {
    if (!searchTerm) return true;
    const searchStr = searchTerm.toLowerCase();
    return ad._id?.toLowerCase().includes(searchStr) ||
      ad.adId?.toLowerCase().includes(searchStr) ||
      ad.id?.toLowerCase().includes(searchStr);
  });

  if (loading) return (
    <div className="flex justify-center items-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 sm:h-10 sm:w-10 md:h-12 md:w-12 border-t-2 border-b-2 border-green-500"></div>
    </div>
  );

  return (
    <div className="p-2 sm:p-4 md:p-6">
      <div className="flex flex-col mb-6 gap-4 mt-2">
        <h1 className="text-xl sm:text-2xl font-black text-slate-800 tracking-tight">
          Completed Advertisements
        </h1>
        {/* Floating Pill Bar for Desktop & Mobile Navigation */}
        <div className="bg-white/90 backdrop-blur-xl shadow-lg shadow-slate-200/40 border border-slate-100 rounded-[1.5rem] sm:rounded-full p-1.5 sm:p-2 w-full mx-auto pointer-events-auto transition-all">
          <div className="flex flex-wrap justify-center sm:justify-start items-center gap-1.5 sm:gap-2 w-full">
            {[
              { path: '/advertiser/leads-status/underReview', label: 'Under Review', activeColor: 'bg-yellow-500', activeShadow: 'shadow-yellow-500/30' },
              { path: '/advertiser/leads-status/pending', label: 'Approved', activeColor: 'bg-blue-500', activeShadow: 'shadow-blue-500/30' },
              { path: '/advertiser/leads-status/accepted', label: 'Accepted', activeColor: 'bg-teal-500', activeShadow: 'shadow-teal-500/30' },
              { path: '/advertiser/leads-status/running', label: 'Running', activeColor: 'bg-emerald-500', activeShadow: 'shadow-emerald-500/30' },
              { path: '/advertiser/leads-status/completed', label: 'Completed', activeColor: 'bg-indigo-500', activeShadow: 'shadow-indigo-500/30', active: true },
              { path: '/advertiser/leads-status/rejected', label: 'Rejected', activeColor: 'bg-rose-500', activeShadow: 'shadow-rose-500/30' }
            ].map((item, idx) => (
              <Link
                key={idx}
                to={item.path}
                className={`
                  flex-1 sm:flex-none flex items-center justify-center px-2 sm:px-4 py-2 sm:py-2.5 
                  rounded-xl sm:rounded-full transition-all duration-300 text-[10px] sm:text-xs font-black uppercase tracking-widest text-center whitespace-nowrap
                  ${item.active
                    ? `${item.activeColor} text-white shadow-md ${item.activeShadow} scale-[1.02]`
                    : 'bg-transparent text-slate-500 hover:bg-slate-50 hover:text-slate-700 active:scale-95'
                  }
                `}
              >
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      </div>

      <div className="mb-4 sm:mb-8">
        <div className="bg-yellow-500 p-3 sm:p-4 md:p-6 rounded-lg sm:rounded-xl shadow-lg text-white">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-xs sm:text-sm md:text-lg font-medium opacity-80">
                Completed Advertisements
              </p>

            </div>
            <FaHourglassHalf className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 opacity-80" />
          </div>
        </div>
      </div>

      {completedAds.length === 0 ? (
        <div className="bg-white rounded-lg sm:rounded-xl shadow-sm p-4 sm:p-6 md:p-8 text-center">
          <FaPlay className="w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 mx-auto text-gray-300 mb-2 sm:mb-3 md:mb-4" />
          <h3 className="text-base sm:text-lg md:text-xl font-semibold text-gray-700">
            No Completed Advertisements
          </h3>
          <p className="text-xs sm:text-sm md:text-base text-gray-500">
            You don't have any completed advertisements at the moment.
          </p>
        </div>
      ) : (
        <>
          {/* Search Bar */}
          <div className="mb-4 sm:mb-6">
            <div className="relative max-w-2xl mx-auto">
              <input
                type="text"
                placeholder="Search by Advertisement ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-11 pr-4 py-3 sm:py-3.5 rounded-2xl border-2 border-slate-100 bg-white focus:outline-none focus:ring-4 focus:ring-yellow-500/10 focus:border-yellow-500 transition-all text-sm sm:text-base shadow-sm font-medium text-slate-700 placeholder:text-slate-400"
              />
              <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
            </div>
          </div>

          {/* Paginated Ads */}
          {(() => {
            const startIdx = (currentPage - 1) * adsPerPage;
            // Sort ads by the latest submission date (newest first)
            const sortedAds = [...filteredAds].sort((a, b) => {
              const getLatestDate = (ad) => {
                if (!ad.proofs || ad.proofs.length === 0) return new Date(0);
                return Math.max(
                  ...ad.proofs.map((proof) =>
                    new Date(proof.submittedAt || 0).getTime()
                  )
                );
              };
              return getLatestDate(b) - getLatestDate(a);
            });
            const paginatedAds = sortedAds.slice(
              startIdx,
              startIdx + adsPerPage
            );

            return paginatedAds.map((ad, index) => (
              <div
                key={ad._id || index}
                className="mb-3 sm:mb-4 md:mb-6 bg-white rounded-lg sm:rounded-xl shadow-lg border border-gray-200 overflow-hidden"
              >
                {/* Compact Header */}
                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-2 sm:p-3 md:p-4">
                  <div className="flex justify-between items-center gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 overflow-hidden">
                        <h3 className="text-xs sm:text-sm md:text-lg font-bold truncate">
                          🆔 {ad.adId || "N/A"} • {ad.adType || "N/A"}
                        </h3>
                        {ad.adId && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigator.clipboard.writeText(ad.adId);
                              toast.success('ID Copied!');
                            }}
                            className="bg-white/20 hover:bg-white/30 text-white rounded p-1 transition-colors flex-shrink-0"
                            title="Copy ID"
                          >
                            <FaCopy className="w-3 h-3 sm:w-4 sm:h-4" />
                          </button>
                        )}
                      </div>
                      <p className="text-blue-100 text-xs sm:text-sm mt-0.5 sm:mt-1 truncate">
                        {ad.mediaType?.toUpperCase()} • {ad.userType?.toUpperCase()} • {ad.adLength}s
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-base sm:text-xl md:text-2xl font-bold">{ad.requiredViews?.toLocaleString() || "N/A"}</div>
                      <div className="text-blue-100 text-xs sm:text-sm">Required Views</div>
                    </div>
                  </div>
                </div>

                {/* Compact Details Grid */}
                <div className="p-2 sm:p-3 md:p-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3 md:gap-4 mb-3 sm:mb-4">
                    <div className="bg-gray-50 p-2 sm:p-3 rounded-lg border border-slate-100 shadow-sm">
                      <div className="text-[10px] sm:text-xs text-gray-500 font-medium">TOTAL COST</div>
                      <div className="text-xs sm:text-sm md:text-lg font-bold text-gray-900">₹{ad.totalCost ? Number(ad.totalCost).toFixed(2) : "N/A"}</div>
                    </div>
                    {ad.startDate && ad.endDate && (
                      <div className="bg-gray-50 p-2 sm:p-3 rounded-lg border border-slate-100 shadow-sm">
                        <div className="text-[10px] sm:text-xs text-gray-500 font-medium">CAMPAIGN</div>
                        <div className="text-xs sm:text-sm font-semibold text-gray-900 truncate">
                          {ad.startDate} to {ad.endDate}
                        </div>
                      </div>
                    )}
                    <div className="bg-gray-50 p-2 sm:p-3 rounded-lg border border-slate-100 shadow-sm">
                      <div className="text-[10px] sm:text-xs text-gray-500 font-medium">LOCATION</div>
                      <div className="text-xs sm:text-sm font-semibold text-gray-900 truncate">
                        {ad.pfState ? `${ad.pfState}${ad.pfCities && ad.pfCities.length > 0 ? `, ${ad.pfCities.join(', ')}` : ''}` : "N/A"}
                      </div>
                    </div>
                    <div className="bg-gray-50 p-2 sm:p-3 rounded-lg border border-slate-100 shadow-sm">
                      <div className="text-[10px] sm:text-xs text-gray-500 font-medium">CREATED</div>
                      <div className="text-[10px] sm:text-xs font-mono font-bold text-gray-600">
                        {ad.createdAt ? formatDate(ad.createdAt) : "N/A"}
                      </div>
                    </div>
                  </div>

                  {/* Media Description */}
                  {ad.mediaDescription && (
                    <div className="mb-3 sm:mb-4 p-2 sm:p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="text-[10px] sm:text-xs text-blue-600 font-medium mb-1">MEDIA DESCRIPTION</div>
                      <DescriptionViewer
                        description={ad.mediaDescription}
                        wordLimit={5}
                        textSize="text-xs sm:text-sm"
                      />
                    </div>
                  )}

                  {/* Refund Information */}
                  {ad.refundInfo && ad.refundInfo.totalRefundAmount > 0 && (
                    <div className={`mb-3 sm:mb-4 p-2 sm:p-3 md:p-4 rounded-lg border-2 ${ad.refundInfo.refundProcessed
                      ? 'bg-green-50 border-green-300'
                      : 'bg-yellow-50 border-yellow-300'
                      }`}>
                      <div className="flex items-center gap-1 sm:gap-2 mb-1 sm:mb-2">
                        <FaCheckCircle className={`w-3 h-3 sm:w-4 sm:h-4 ${ad.refundInfo.refundProcessed ? 'text-green-600' : 'text-yellow-600'}`} />
                        <h4 className="text-xs sm:text-sm font-bold text-gray-800">
                          {ad.refundInfo.refundProcessed ? '💰 Refund Processed' : '⚠️ Incomplete Work - No Refund'}
                        </h4>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3">
                        <div>
                          <div className="text-[10px] sm:text-xs text-gray-600 font-medium">REFUND AMOUNT</div>
                          <div className={`text-xs sm:text-sm md:text-lg font-bold ${ad.refundInfo.refundProcessed ? 'text-green-900' : 'text-yellow-900'}`}>
                            ₹{ad.refundInfo.totalRefundAmount.toFixed(2)}
                          </div>
                        </div>
                        <div>
                          <div className="text-[10px] sm:text-xs text-gray-600 font-medium">COMPLETED WORK</div>
                          <div className="text-xs sm:text-sm md:text-lg font-bold text-gray-900">
                            {ad.refundInfo.completedReporters || 0}/{ad.requiredReporter || 0}
                          </div>
                        </div>
                        <div>
                          <div className="text-[10px] sm:text-xs text-gray-600 font-medium">INCOMPLETE</div>
                          <div className="text-xs sm:text-sm md:text-lg font-bold text-red-900">
                            {ad.refundInfo.refundedReporters || 0}
                          </div>
                        </div>
                        <div>
                          <div className="text-[10px] sm:text-xs text-gray-600 font-medium">STATUS</div>
                          <div className={`text-[10px] sm:text-xs md:text-sm font-bold ${ad.refundInfo.refundProcessed ? 'text-green-900' : 'text-yellow-900'}`}>
                            {ad.refundInfo.refundProcessed ? 'Credited to Wallet' : 'Admin Skipped Refund'}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Performance Summary & Check Button */}
                  {Array.isArray(ad.proofs) && ad.proofs.length > 0 && (
                    <div className="mb-3 sm:mb-4">
                      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-0 mb-2 sm:mb-3">
                        <h4 className="text-xs sm:text-sm md:text-md font-semibold text-gray-800 flex items-center gap-2">
                          📊 Performance Summary
                        </h4>
                        <button
                          onClick={() => checkVideoViews(ad.adId)}
                          disabled={checkingViews[ad.adId]}
                          className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1 sm:py-2 bg-blue-600 text-white rounded text-xs sm:text-sm hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {checkingViews[ad.adId] ? (
                            <>
                              <FaSpinner className="animate-spin w-3 h-3 sm:w-4 sm:h-4" />
                              <span className="hidden sm:inline">Checking...</span>
                              <span className="sm:hidden">...</span>
                            </>
                          ) : (
                            <>
                              <FaEye className="w-3 h-3 sm:w-4 sm:h-4" />
                              <span className="hidden sm:inline">Check My Work</span>
                              <span className="sm:hidden">Check</span>
                            </>
                          )}
                        </button>
                      </div>

                      <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-3 sm:mb-4">
                        <div className="bg-green-50 p-2 sm:p-3 rounded-lg border border-green-200 text-center">
                          <div className="text-[10px] sm:text-xs text-green-600 font-medium">REQUIRED</div>
                          <div className="text-xs sm:text-sm md:text-lg font-bold text-green-900">{ad.requiredViews?.toLocaleString() || "N/A"}</div>
                        </div>
                        <div className="bg-blue-50 p-2 sm:p-3 rounded-lg border border-blue-200 text-center">
                          <div className="text-[10px] sm:text-xs text-blue-600 font-medium">SUBMISSIONS</div>
                          <div className="text-xs sm:text-sm md:text-lg font-bold text-blue-900">{ad.proofs.length}</div>
                        </div>
                        <div className="bg-emerald-50 p-2 sm:p-3 rounded-lg border border-emerald-200 text-center">
                          <div className="text-[10px] sm:text-xs text-emerald-600 font-medium">STATUS</div>
                          <div className="text-xs sm:text-sm md:text-lg font-bold text-emerald-900">✅ <span className="hidden sm:inline">Completed</span><span className="sm:hidden">Done</span></div>
                        </div>
                      </div>

                      {/* Video Views Results */}
                      {viewResults[ad.adId] && (
                        <div className="bg-white border border-gray-200 rounded-lg p-2 sm:p-3 md:p-4 mb-3 sm:mb-4">
                          <h5 className="text-xs sm:text-sm md:text-md font-semibold text-gray-800 mb-2 sm:mb-3 flex items-center gap-1 sm:gap-2">
                            <FaEye className="w-3 h-3 sm:w-4 sm:h-4" />
                            Video Views Analysis
                          </h5>

                          {ad.proofs.some(p => p.platform?.toLowerCase() === 'instagram') && (
                            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-xs sm:text-sm text-red-600 font-bold text-left flex items-start gap-2 shadow-sm">
                              <span className="text-lg leading-none">⚠️</span>
                              <span>For Instagram platforms, please watch the video to check views manually. Automatic view checking is not supported.</span>
                            </div>
                          )}

                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3 mb-3 sm:mb-4">
                            <div className="bg-blue-50 p-2 sm:p-3 rounded-lg text-center">
                              <div className="text-[10px] sm:text-xs text-blue-600 font-medium">TOTAL VIEWS</div>
                              <div className="text-xs sm:text-sm md:text-lg font-bold text-blue-900">
                                {viewResults[ad.adId].performanceMetrics.totalViewsAchieved.toLocaleString()}
                              </div>
                            </div>
                            <div className="bg-green-50 p-2 sm:p-3 rounded-lg text-center">
                              <div className="text-[10px] sm:text-xs text-green-600 font-medium">ACHIEVEMENT</div>
                              <div className="text-xs sm:text-sm md:text-lg font-bold text-green-900">
                                {viewResults[ad.adId].performanceMetrics.achievementPercentage}%
                              </div>
                            </div>
                            <div className="bg-purple-50 p-2 sm:p-3 rounded-lg text-center">
                              <div className="text-[10px] sm:text-xs text-purple-600 font-medium">DIFFERENCE</div>
                              <div className={`text-xs sm:text-sm md:text-lg font-bold ${viewResults[ad.adId].performanceMetrics.viewsDifference >= 0
                                ? 'text-green-600'
                                : 'text-red-600'
                                }`}>
                                {viewResults[ad.adId].performanceMetrics.viewsDifference >= 0 ? '+' : ''}
                                {viewResults[ad.adId].performanceMetrics.viewsDifference.toLocaleString()}
                              </div>
                            </div>
                            <div className="bg-orange-50 p-2 sm:p-3 rounded-lg text-center">
                              <div className="text-[10px] sm:text-xs text-orange-600 font-medium">SUCCESS RATE</div>
                              <div className="text-xs sm:text-sm md:text-lg font-bold text-orange-900">
                                {Math.round(viewResults[ad.adId].performanceMetrics.successRate)}%
                              </div>
                            </div>
                          </div>

                          {/* Individual Video Results - Compact Table */}
                          <div className="overflow-x-auto">
                            <table className="min-w-full bg-white border border-gray-200 rounded-lg text-xs sm:text-sm">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-1 sm:px-2 py-1 sm:py-2 text-left text-[10px] sm:text-xs font-semibold text-gray-600 uppercase">Status</th>
                                  <th className="px-1 sm:px-2 py-1 sm:py-2 text-left text-[10px] sm:text-xs font-semibold text-gray-600 uppercase">Platform</th>
                                  <th className="px-1 sm:px-2 py-1 sm:py-2 text-left text-[10px] sm:text-xs font-semibold text-gray-600 uppercase">Channel</th>
                                  <th className="px-1 sm:px-2 py-1 sm:py-2 text-left text-[10px] sm:text-xs font-semibold text-gray-600 uppercase">ID</th>
                                  <th className="px-1 sm:px-2 py-1 sm:py-2 text-right text-[10px] sm:text-xs font-semibold text-gray-600 uppercase">Views</th>
                                  <th className="px-1 sm:px-2 py-1 sm:py-2 text-center text-[10px] sm:text-xs font-semibold text-gray-600 uppercase">Link</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200">
                                {viewResults[ad.adId].viewResults.map((result, index) => (
                                  <tr key={index} className="hover:bg-gray-50 transition-colors">
                                    <td className="px-1 sm:px-2 py-1 sm:py-2 text-center">
                                      {result.platform?.toLowerCase() === 'instagram' ? (
                                        <span className="text-amber-500 font-bold text-xs" title="Instagram requires manual checking">⚠️</span>
                                      ) : result.status === 'success' ? (
                                        <FaCheckCircle className="text-green-500 text-xs sm:text-sm mx-auto" />
                                      ) : (
                                        <FaTimesCircle className="text-red-500 text-xs sm:text-sm mx-auto" />
                                      )}
                                    </td>
                                    <td className="px-1 sm:px-2 py-1 sm:py-2">
                                      <span className="inline-flex items-center px-1 sm:px-2 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-medium bg-blue-100 text-blue-800">
                                        {result.platform}
                                      </span>
                                    </td>
                                    <td className="px-1 sm:px-2 py-1 sm:py-2">
                                      <div className="text-[10px] sm:text-xs font-medium text-gray-900 max-w-[100px] sm:max-w-[150px] truncate" title={result.channelName}>
                                        {result.channelName}
                                      </div>
                                    </td>
                                    <td className="px-1 sm:px-2 py-1 sm:py-2">
                                      <span className="text-[10px] sm:text-xs font-mono text-gray-600 bg-gray-100 px-1 sm:px-2 py-0.5 sm:py-1 rounded">
                                        {result.iinsafId}
                                      </span>
                                    </td>
                                    <td className="px-1 sm:px-2 py-1 sm:py-2 text-right">
                                      <div className="text-[10px] sm:text-xs font-bold text-gray-900">
                                        {result.platform?.toLowerCase() === 'instagram' ? (
                                          <span className="text-red-600 font-semibold">Check Manually</span>
                                        ) : (
                                          result.views.toLocaleString()
                                        )}
                                      </div>
                                    </td>
                                    <td className="px-1 sm:px-2 py-1 sm:py-2 text-center">
                                      {result.videoLink && result.videoLink !== "N/A" ? (
                                        <a
                                          href={result.videoLink}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="inline-flex items-center px-1 sm:px-2 py-0.5 sm:py-1 rounded text-[10px] sm:text-xs font-medium bg-green-100 text-green-800 hover:bg-green-200 transition-colors"
                                        >
                                          <FaPlay className="mr-0.5 sm:mr-1 w-2 h-2 sm:w-3 sm:h-3" />
                                          <span className="hidden sm:inline">Watch</span>
                                        </a>
                                      ) : (
                                        <span className="text-[10px] sm:text-xs text-gray-400">N/A</span>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Compact Reporters Table */}
                  {Array.isArray(ad.proofs) && ad.proofs.length > 0 ? (
                    <>
                      <div className="hidden lg:block overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 text-xs sm:text-sm border border-slate-200 rounded-lg">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-1.5 sm:px-2 md:px-3 py-1.5 sm:py-2 text-left text-[10px] sm:text-xs font-semibold text-gray-600 uppercase">iinsaf ID</th>
                              <th className="px-1.5 sm:px-2 md:px-3 py-1.5 sm:py-2 text-left text-[10px] sm:text-xs font-semibold text-gray-600 uppercase">Platform</th>
                              <th className="px-1.5 sm:px-2 md:px-3 py-1.5 sm:py-2 text-left text-[10px] sm:text-xs font-semibold text-gray-600 uppercase">Channel</th>
                              <th className="px-1.5 sm:px-2 md:px-3 py-1.5 sm:py-2 text-left text-[10px] sm:text-xs font-semibold text-gray-600 uppercase">Video Link</th>
                              <th className="px-1.5 sm:px-2 md:px-3 py-1.5 sm:py-2 text-left text-[10px] sm:text-xs font-semibold text-gray-600 uppercase">Proof Screenshot</th>
                              <th className="px-1.5 sm:px-2 md:px-3 py-1.5 sm:py-2 text-left text-[10px] sm:text-xs font-semibold text-gray-600 uppercase">Duration</th>
                              <th className="px-1.5 sm:px-2 md:px-3 py-1.5 sm:py-2 text-left text-[10px] sm:text-xs font-semibold text-gray-600 uppercase">Submitted</th>
                              <th className="px-1.5 sm:px-2 md:px-3 py-1.5 sm:py-2 text-left text-[10px] sm:text-xs font-semibold text-gray-600 uppercase">Status</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200 border-t border-slate-200">
                            {[...ad.proofs]
                              .sort((a, b) => new Date(b.submittedAt || 0) - new Date(a.submittedAt || 0))
                              .map((proof, proofIndex) => (
                                <tr key={proofIndex} className={`hover:bg-gray-50 transition-colors ${proofIndex === 0 ? "bg-green-50/30 border-l-4 border-l-green-400" : ""
                                  }`}>
                                  <td className="px-1.5 sm:px-2 md:px-3 py-2 text-[10px] sm:text-xs md:text-sm font-bold text-gray-900 border-b border-r border-slate-100 tracking-tight">
                                    {proof.iinsafId}
                                  </td>
                                  <td className="px-1.5 sm:px-2 md:px-3 py-2 border-b border-r border-slate-100">
                                    <span className="inline-flex items-center px-2 py-1 rounded-lg text-[10px] sm:text-xs font-black uppercase tracking-wider bg-blue-50 text-blue-600 border border-blue-100">
                                      {proof.platform}
                                    </span>
                                  </td>
                                  <td className="px-1.5 sm:px-2 md:px-3 py-2 text-[10px] sm:text-xs md:text-sm font-medium text-gray-900 max-w-[100px] sm:max-w-[150px] truncate border-b border-r border-slate-100" title={proof.channelName}>
                                    {proof.channelName}
                                  </td>
                                  <td className="px-1.5 sm:px-2 md:px-3 py-2 border-b border-r border-slate-100">
                                    {proof.videoLink ? (
                                      <a
                                        href={proof.videoLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center px-2.5 py-1.5 rounded-lg text-xs font-bold bg-indigo-50 text-indigo-600 hover:bg-indigo-100 border border-indigo-100 transition-colors"
                                      >
                                        <FaPlay className="mr-1.5 w-2.5 h-2.5" />
                                        <span>Watch Video</span>
                                      </a>
                                    ) : (
                                      <span className="text-gray-400 text-[10px] sm:text-xs font-bold bg-slate-100 px-2 py-1 rounded">N/A</span>
                                    )}
                                  </td>
                                  <td className="px-1.5 sm:px-2 md:px-3 py-2 border-b border-r border-slate-100">
                                    {proof.completedTaskScreenshot ? (
                                      <a
                                        href={proof.completedTaskScreenshot}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center px-2.5 py-1.5 rounded-lg text-xs font-bold bg-slate-800 text-white hover:bg-slate-700 shadow-sm transition-colors"
                                      >
                                        <FaImage className="mr-1.5 w-3 h-3" />
                                        <span>Screenshot</span>
                                      </a>
                                    ) : (
                                      <span className="text-gray-400 text-[10px] sm:text-xs font-bold bg-slate-100 px-2 py-1 rounded">N/A</span>
                                    )}
                                  </td>
                                  <td className="px-1.5 sm:px-2 md:px-3 py-2 text-[10px] sm:text-xs font-bold text-gray-600 bg-slate-50 border-b border-r border-slate-100">
                                    {proof.duration || "N/A"}
                                  </td>
                                  <td className="px-1.5 sm:px-2 md:px-3 py-2 text-[10px] sm:text-xs font-mono font-bold text-slate-500 bg-slate-50 border-b border-r border-slate-100">
                                    {proof.submittedAt ? formatDate(proof.submittedAt) : "N/A"}
                                  </td>
                                  <td className="px-1.5 sm:px-2 md:px-3 py-2 border-b border-slate-100">
                                    <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-[10px] sm:text-xs font-bold bg-emerald-50 text-emerald-600 border border-emerald-100">
                                      <FaCheckCircle className="w-2.5 h-2.5 mr-1" />
                                      {proof.status || "Completed"}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Mobile Card Layout for Submissions */}
                      <div className="lg:hidden flex flex-col gap-3 mt-2 px-1">
                        {[...ad.proofs]
                          .sort((a, b) => new Date(b.submittedAt || 0) - new Date(a.submittedAt || 0))
                          .map((proof, proofIndex) => (
                            <div key={proofIndex} className={`bg-white rounded-xl shadow-sm border p-3 flex flex-col gap-3 relative ${proofIndex === 0 ? "border-green-300" : "border-slate-100"}`}>
                              {/* Header: Platform & Time */}
                              <div className="flex justify-between items-start border-b border-slate-50 pb-2">
                                <div>
                                  <div className="flex items-center gap-1.5 mb-1">
                                    <p className="text-xs font-black text-slate-800 uppercase tracking-widest">{proof.platform || "N/A"}</p>
                                    <span className="text-[9px] font-bold text-slate-500 bg-slate-100 border border-slate-200 px-1.5 py-0.5 rounded ml-1">ID: {proof.iinsafId}</span>
                                  </div>
                                  <p className="text-[10px] font-bold text-slate-500 max-w-[200px] truncate">{proof.channelName || "N/A"}</p>
                                </div>
                                <div className="flex flex-col items-end gap-1">
                                  <span className="text-[9px] font-bold text-slate-400 font-mono bg-slate-50 px-2 py-0.5 rounded border border-slate-100">
                                    {proof.submittedAt ? formatDate(proof.submittedAt) : "N/A"}
                                  </span>
                                  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded border bg-emerald-50 border-emerald-200 text-emerald-600 inline-flex items-center gap-1">
                                    <FaCheckCircle className="w-2 h-2" /> {proof.status || "Completed"}
                                  </span>
                                </div>
                              </div>

                              {/* Body: Actions & Data */}
                              <div className="flex flex-wrap items-center justify-between gap-2">
                                {/* Left side actions */}
                                <div className="flex flex-col gap-1.5">
                                  {proof.videoLink && (
                                    <a href={proof.videoLink} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 text-indigo-600 border border-indigo-100 rounded-lg text-xs hover:bg-indigo-100 w-fit font-bold shadow-sm active:scale-95 transition-all">
                                      <FaPlay className="w-2.5 h-2.5" /> Watch Video
                                    </a>
                                  )}
                                  {proof.completedTaskScreenshot && (
                                    <a href={proof.completedTaskScreenshot} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 text-white rounded-lg text-xs font-bold hover:bg-slate-700 shadow-sm active:scale-95 transition-all w-fit">
                                      <FaImage className="w-3 h-3" /> Screenshot
                                    </a>
                                  )}
                                </div>

                                {/* Right side data */}
                                <div className="flex flex-col items-end gap-1.5">
                                  <div className="text-[10px] font-bold text-gray-600 bg-slate-100 border border-slate-200 px-2.5 py-1 rounded w-fit flex items-center gap-1">
                                    <FaHourglassHalf className="w-2.5 h-2.5 text-slate-400" /> {proof.duration || "N/A"}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                      </div>
                    </>
                  ) : (
                    <div className="p-3 sm:p-4 text-center text-xs sm:text-sm text-gray-500 bg-gray-50 rounded-lg">
                      No reporter proofs submitted for this ad.
                    </div>
                  )}
                </div>
              </div>
            ));
          })()}

          {/* Show empty state if nothing matches search but list is not entirely empty overall */}
          {completedAds.length > 0 && filteredAds.length === 0 && (
            <div className="bg-white rounded-lg sm:rounded-xl shadow-sm p-4 sm:p-6 md:p-8 text-center text-slate-500 mt-4 border border-gray-200">
              No advertisements found matching "{searchTerm}"
            </div>
          )}

          {/* Pagination Controls */}
          {(() => {
            const totalPages = Math.ceil(filteredAds.length / adsPerPage);
            if (totalPages <= 1) return null;

            return (
              <div className="flex justify-center mt-3 sm:mt-6 mb-3 sm:mb-6 px-2 sm:px-4">
                <nav className="flex flex-col sm:flex-row items-center justify-center space-y-2 sm:space-y-0 sm:space-x-2">
                  {/* Previous Button */}
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`px-2 sm:px-3 md:px-4 py-1 sm:py-1.5 md:py-2 rounded border border-yellow-400 text-yellow-800 bg-white hover:bg-yellow-100 transition-colors font-medium text-xs sm:text-sm md:text-base ${currentPage === 1 ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                  >
                    Prev
                  </button>

                  {/* Current Page Display */}
                  <div className="flex items-center space-x-1 px-2">
                    <span className="px-2 sm:px-3 md:px-4 py-1 sm:py-1.5 md:py-2 rounded border border-yellow-400 bg-yellow-300 text-yellow-900 font-bold text-center text-xs sm:text-sm md:text-base min-w-[40px] sm:min-w-[60px]">
                      {currentPage}
                    </span>
                  </div>

                  {/* Next Button */}
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`px-2 sm:px-3 md:px-4 py-1 sm:py-1.5 md:py-2 rounded border border-yellow-400 text-yellow-800 bg-white hover:bg-yellow-100 transition-colors font-medium text-xs sm:text-sm md:text-base ${currentPage === totalPages
                      ? "opacity-50 cursor-not-allowed"
                      : ""
                      }`}
                  >
                    Next
                  </button>
                </nav>
              </div>
            );
          })()}
        </>
      )}
    </div>
  );
};

export default CompletedAdvertisements;
